<template>
    <BaseDialog
        v-model="dialogOpen"
        width="600"
        :title="dialogTitle"
        header-color="error"
        titleClass="tw-text-xl"
    >
        <div class="tw-flex tw-flex-col tw-gap-4">
            <div>
                <span> {{ $t('message.unlinkSubAccount') }}</span>
                <ul class="text--secondary tw-mt-4 tw-font-bold">
                    <li v-for="(item, idx) in unlinkDesc" :key="idx">
                        {{ item }}
                    </li>
                </ul>
            </div>
            <div class="tw-flex tw-flex-row tw-items-center tw-gap-4">
                <hr class="text--disabled tw-w-1/2" />
                <span class="text--disabled tw-text-xs tw-font-bold"> OR </span>
                <hr class="text--disabled tw-w-1/2" />
            </div>
            <div>
                <span>{{ $t('message.deleteSubAccount') }}</span>
                <ul class="text--secondary tw-mt-4 tw-font-bold">
                    <li v-for="(item, idx) in deleteDesc" :key="idx">
                        {{ item }}
                    </li>
                </ul>
            </div>
            <span class="tw-text-red-500">
                {{ $t('message.confirmDeletion') }}
            </span>
        </div>
        <div
            class="tw-mt-4 tw-flex tw-flex-col tw-justify-end tw-gap-4 md:tw-flex-row"
        >
            <v-btn variant="outlined" @click="cancel">
                {{ $t('message.cancel') }}
            </v-btn>
            <v-btn color="warning" @click="unlink"
                >{{ $t('message.unlinkAccount') }}
            </v-btn>
            <v-btn color="error" @click="remove" :loading="loading"
                >{{ $t('message.delete') }}
            </v-btn>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseDialog from '@/components/base/dialog/BaseDialog.vue'
import { useToast } from '@/components/base/toast/composable/useToast'
import { deleteSubAccount } from '../api/deleteSubAccountApi'
import type { AccountDetail } from '@/components/bookclass/api/getSubAccountByUserApi'
import { DEFAULT_ERROR_MESSAGE } from '@/store/moduleComponents/authentication/actionsAuth'
import { useI18n } from 'vue-i18n-composable'

const props = defineProps<{
    isOpen: boolean
    subAccount: AccountDetail
    mainAccountName: string
}>()
const { t } = useI18n()
const unlinkDesc = computed(() => {
    const template = t('message.unlinkSubAccountDesc', '', {
        returnObjects: true,
    })
    return template.map((line) =>
        line.replace('%{name}', props.subAccount.fullName)
    )
})
const deleteDesc = computed(() => {
    const template = t('message.deleteSubAccountDesc', '', {
        returnObjects: true,
    })
    return template.map((line) =>
        line.replace('%{name}', props.subAccount.fullName)
    )
})

const dialogTitle = computed(() => {
    const template = t('message.deleteSubAccountTitle', {
        name: props.subAccount.fullName,
    })
    return template
})

const emit = defineEmits(['onClose', 'onSuccess', 'onClickUnlinkInstead'])

const close = () => {
    emit('onClose')
}

const success = () => {
    emit('onSuccess')
}

const unlink = () => {
    emit('onClickUnlinkInstead')
}

const dialogOpen = ref(props.isOpen)
watch(
    () => props.isOpen,
    (newValue) => {
        dialogOpen.value = newValue
    }
)
watch(dialogOpen, (newValue) => {
    if (!newValue && props.isOpen) {
        close()
    }
})

const cancel = () => {
    apiError.value = ''
    close()
}

const loading = ref(false)
const apiError = ref('')

const { showSuccessToast, showErrorToast } = useToast()
const remove = async () => {
    loading.value = true
    try {
        const result = await deleteSubAccount({
            subAccountId: props.subAccount.id,
        })

        if (!result.success) {
            throw new Error(result.error ?? DEFAULT_ERROR_MESSAGE)
        }

        showSuccessToast(
            t('message.successfullyRemovedSubAccount', {
                name: props.subAccount.fullName,
            })
        )
        cancel()
        success()
    } catch (error: any) {
        apiError.value = error.message ?? error?.toString()
        showErrorToast(apiError.value)
    } finally {
        loading.value = false
    }
}
</script>
